import React, { useCallback, useEffect, useMemo } from "react";
import queryStringParser from "query-string";
import { Box } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { Route, RouteComponentProps } from "react-router";
import clsx from "clsx";
import { useDeviceTypes } from "halifax";
import { CheckoutStateProvider } from "@capone/checkout";

import { CoreActivitiesCheckout, VIEWED_ACTIVITIES_CHECKOUT } from "redmond";
import { useIsTravelApp } from "@capone/common";
import { useDispatch, useSelector } from "react-redux";
import { ActivityBookConnectorProps } from "./container";
import {
  DesktopActivityBookWorkflow,
  MobileActivityBookWorkflow,
  ConfirmationPage,
  BookingErrorModal,
  BookingInProgressModal,
} from "./components";
import {
  PATH_BOOK,
  PATH_BOOK_CONFIRMATION,
  PATH_SHOP,
} from "../../utils/paths";
import "./styles.scss";
import { activityBookStateMachine } from "./state/stateMachine";
import { getInitialActivityMachineContext } from "./state/initialContext";
import { Actions } from "./state/actions";
import * as services from "./state/services";
import { Guards } from "./state/guards";
import {
  populateCheckoutQueryParams,
  transitionStateOnPathnameChange,
  validateContext,
} from "./state/utils";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { useExperiments } from "../../context/experiments";
import {
  setExperiencesShopDetails,
  setExperiencesShopOptions,
  setTravelerCounts,
  setSelectedBookableItem,
  setSelectedDate,
  setSelectedTime,
} from "../shop/actions/actions";
import { setRewardsAccounts } from "../rewards/actions/actions";
import {
  mapCoreShopDetails,
  mapCorePriceBreakdowns,
  mapCoreTravelerCounts,
  findCoreSelectedBookableItem,
} from "../../utils/coreDataAdapter";
import { actions as searchActions } from "../search/actions";
import { getEntryType, getEntryTypesAll, getLocation as getSearchLocation } from "../search/reducer";
import { resolveEntryTypesFromQueryString } from "./utils/resolveEntryTypesFromQueryString";
import {
  getLocationNameToResolve,
  fetchLocationForBookPage,
} from "./utils/resolveLocationFromQueryString";

export interface IActivityBookProps
  extends RouteComponentProps,
  ActivityBookConnectorProps {
  core?: CoreActivitiesCheckout;
}

export const ActivityBook = (props: IActivityBookProps) => {
  const {
    location,
    shopDetails,
    priceBreakdown,
    travelerCounts,
    rewardsAccounts,
    shopOptionTrackingProperties,
    activityDetailsProperties,
    opaqueProductToken,
    selectedBookableItem,
    core,
  } = props;

  const { matchesMobile, matchesDesktop } = useDeviceTypes();
  const history = useHistory();
  const expState = useExperiments();
  const dispatch = useDispatch();

  // Built synchronously so the mount-frozen machine context below sees core data.
  const coreValues = useMemo(() => {
    if (core == null) return null;

    const mappedShopDetails = mapCoreShopDetails(core);
    const mappedPriceBreakdowns = mapCorePriceBreakdowns(core);
    const mappedTravelerCounts = mapCoreTravelerCounts(core);
    const mappedSelectedItem = findCoreSelectedBookableItem(
      mappedShopDetails.response,
      core.selectedBookableItemId,
    );

    const selectedPriceBreakdown =
      core.selectedStartTime != null
        ? mappedPriceBreakdowns.find(
            (pb) => pb.startTime === core.selectedStartTime,
          ) ?? mappedPriceBreakdowns[0]
        : mappedPriceBreakdowns[0];

    // b2bportal's ShopOption tracking omits these; legacy v0 includes them.
    const coreShopOptionTrackingProperties = {
      ...core.shopOptionSummary.trackingProperties,
      properties: {
        ...core.shopOptionSummary.trackingProperties.properties,
        activity_option_name: mappedSelectedItem?.title,
        option_code: mappedSelectedItem?.experienceOptionId?.value,
      },
    };

    const { locationName, inDestination, inDestinationLane } =
      queryStringParser.parse(history.location.search);
    const resolvedEntryTypes = resolveEntryTypesFromQueryString({
      search: history.location.search,
      existingEntryType: undefined,
      existingEntryTypesAll: undefined,
    });

    const coreActivityDetailsProperties = {
      properties: {
        ...mappedShopDetails.response.trackingProperties?.properties,
        activity_provider: mappedShopDetails.response.supplierInfo.name,
        avg_rating: mappedShopDetails.response.rating.reviewAverage,
        activity_name: mappedShopDetails.response.name,
        number_of_photos: mappedShopDetails.response.additionalImages.length,
        number_of_reviews: mappedShopDetails.response.rating.numberOfReviews,
        productId: mappedShopDetails.response.id.value,
        product_sort_index: mappedShopDetails.activtyIndex,
        ...coreShopOptionTrackingProperties.properties,
        // URL values last so the backend spreads above can't overwrite them.
        entry_type: resolvedEntryTypes?.entryType,
        entry_types_all: resolvedEntryTypes?.entryTypesAll,
        in_destination: inDestination === "true",
        in_destination_lane:
          typeof inDestinationLane === "string" ? inDestinationLane : undefined,
        location_searched:
          typeof locationName === "string"
            ? locationName
            : mappedShopDetails.response.searchLocation,
      },
      encryptedProperties: [
        mappedShopDetails.response.trackingProperties?.encryptedProperties ?? "",
        coreShopOptionTrackingProperties.encryptedProperties ?? "",
      ],
    };

    return {
      shopDetails: mappedShopDetails,
      priceBreakdown: selectedPriceBreakdown,
      priceBreakdowns: mappedPriceBreakdowns,
      travelerCounts: mappedTravelerCounts,
      selectedBookableItem: mappedSelectedItem,
      opaqueToken: core.shopOptionSummary.opaqueActivityToken,
      shopOptionTrackingProperties: coreShopOptionTrackingProperties,
      activityDetailsProperties: coreActivityDetailsProperties,
    };
  }, [core]);  

  useEffect(() => {
    if (core == null || coreValues == null) return;

    dispatch(setExperiencesShopDetails(coreValues.shopDetails));
    dispatch(
      setExperiencesShopOptions(
        coreValues.priceBreakdowns,
        coreValues.shopOptionTrackingProperties,
        coreValues.opaqueToken,
      ),
    );
    dispatch(setTravelerCounts(coreValues.travelerCounts));
    dispatch(setSelectedBookableItem(coreValues.selectedBookableItem));

    if (core.selectedDate != null) {
      dispatch(setSelectedDate(core.selectedDate));
    }
    if (core.selectedStartTime != null) {
      dispatch(setSelectedTime(core.selectedStartTime));
    }

    dispatch(setRewardsAccounts(core.rewardsAccounts));
  }, []);  

  const existingEntryType = useSelector(getEntryType);
  const existingEntryTypesAll = useSelector(getEntryTypesAll);

  useEffect(() => {
    // Direct Book-page loads (e.g. under the core-activities funnel) never go
    // through the Shop page's fetchExperiencesShopSaga, so entryType/entryTypesAll
    // forwarded on the checkout URL would otherwise never reach Redux.
    const resolved = resolveEntryTypesFromQueryString({
      search: history.location.search,
      existingEntryType,
      existingEntryTypesAll,
    });
    if (!resolved) return;

    dispatch(searchActions.setEntryTypesAll(resolved.entryTypesAll));
    dispatch(searchActions.setEntryType(resolved.entryType));
  }, []);  

  const existingSearchLocation = useSelector(getSearchLocation);

  useEffect(() => {
    // Mirrors fetchExperiencesAvailabilitySaga's location resolution for direct
    // Book-page loads, so location_searched on checkout tracking events isn't
    // silently empty when Availability never ran this session.
    const locationName = getLocationNameToResolve({
      search: history.location.search,
      existingLocation: existingSearchLocation,
    });
    if (!locationName) return;

    fetchLocationForBookPage(locationName)
      .then((resolvedLocation) => {
        if (resolvedLocation) {
          dispatch(searchActions.setLocation(resolvedLocation));
        }
      })
      .catch(() => {
        // Non-critical — checkout can proceed without a resolved search location.
      });
  }, []);  

  if (!shopDetails && core == null) {
    history.push(`${PATH_SHOP}${history.location.search}`);
  }

  const { isTravelApp } = useIsTravelApp();

  const channel = useMemo(() => {
    if (matchesDesktop) return "Web";
    return isTravelApp ? "TravelApp" : "Mobile";
  }, [matchesDesktop, isTravelApp]);

  const effectiveShopDetails =
    coreValues != null ? coreValues.shopDetails.response : shopDetails;
  const effectivePriceBreakdown =
    coreValues != null ? coreValues.priceBreakdown : priceBreakdown;
  const effectiveTravelerCounts =
    coreValues != null ? coreValues.travelerCounts : travelerCounts;
  const effectiveRewardsAccounts =
    coreValues != null ? core?.rewardsAccounts : rewardsAccounts;
  const effectiveTrackingProperties =
    coreValues != null
      ? coreValues.shopOptionTrackingProperties
      : shopOptionTrackingProperties;
  const effectiveOpaqueToken =
    coreValues != null ? coreValues.opaqueToken : opaqueProductToken;
  const effectiveSelectedBookableItem =
    coreValues != null ? coreValues.selectedBookableItem : selectedBookableItem;
  const effectiveActivityDetailsProperties =
    coreValues != null
      ? coreValues.activityDetailsProperties
      : activityDetailsProperties;

  useEffect(() => {
    const { properties, encryptedProperties } = effectiveActivityDetailsProperties;
    trackEvent({
      eventName: VIEWED_ACTIVITIES_CHECKOUT,
      properties,
      encryptedProperties,
    });
  }, []);

   
  const getInitialActivityMachineContextCallBack = useCallback(
    () =>
      getInitialActivityMachineContext(
        effectiveShopDetails,
        effectivePriceBreakdown,
        effectiveTravelerCounts,
        effectiveRewardsAccounts,
        effectiveTrackingProperties,
        effectiveActivityDetailsProperties,
        channel,
        expState.experiments,
        effectiveOpaqueToken,
        effectiveSelectedBookableItem,
      ),
    [],
  );

  return (
    <CheckoutStateProvider
      stateMachine={activityBookStateMachine}
      actions={Actions}
      guards={Guards}
      context={getInitialActivityMachineContextCallBack()}
      services={services}
      getInitialContext={getInitialActivityMachineContextCallBack}
      validateContext={validateContext as (ctx: unknown) => boolean}
      history={history}
      onStateValueChange={
        matchesMobile && isTravelApp ? populateCheckoutQueryParams : undefined
      }
      onPathnameChange={
        matchesMobile && isTravelApp
          ? transitionStateOnPathnameChange
          : undefined
      }
    >
      <Box
        className={clsx(
          "activity-book-root",
          {
            confirm: { confirm: location.pathname === PATH_BOOK_CONFIRMATION },
          },
          { mobile: matchesMobile },
        )}
      >
        <Route path={PATH_BOOK} exact>
          <Box>
            {matchesDesktop && <DesktopActivityBookWorkflow />}
            {matchesMobile && <MobileActivityBookWorkflow />}
          </Box>
          <BookingErrorModal />
          <BookingInProgressModal />
        </Route>
        <Route path={[PATH_BOOK_CONFIRMATION]}>
          <ConfirmationPage />
        </Route>
      </Box>
    </CheckoutStateProvider>
  );
};
